/**
 * テナント/休日ページ (/tenant/holiday)
 *
 * サイドバーメニュー「テナント」配下のタブとして表示される。
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { TabBar } from "../components/molecules/TabBar";
import { ActionIcon } from "../components/atoms/Icon";
import { useMenuTitle } from "../shared/menu/useMenuTitle";
import DayOffTypeTable, { type DayOffTypeRow } from "../components/DayOffTypeTable";
import { fetchDayOffTypes, type DayOffTypeResponse } from "../api/dayOffTypes";
import { usePermission } from "../hooks/usePermission";

const DEFAULT_TEXT = "#1f2937";

const toRow = (dt: DayOffTypeResponse): DayOffTypeRow => ({
    id: dt.id,
    code: dt.code,
    name: dt.name,
    short_name: dt.short_name ?? "",
    description: dt.description ?? "",
    background_color: dt.background_color ?? "",
    text_color: dt.text_color ?? DEFAULT_TEXT,
    is_work_time: dt.is_work_time,
    day_count_type: dt.day_count_type,
    is_system: dt.is_system,
});

const TenantHolidayPage = () => {
    const menuTitle = useMenuTitle();
    const navigate = useNavigate();
    const { canEdit } = usePermission();
    const [rows, setRows] = useState<DayOffTypeRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            try {
                const response = await fetchDayOffTypes();
                setRows(response.items.map(toRow));
            } catch (error) {
                console.error("Failed to fetch day off types:", error);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, []);

    const handleEditRow = (row: DayOffTypeRow) => {
        navigate(`/tenant/holiday/${row.id}/edit`);
    };

    return (
        <div className="flex flex-1 flex-col overflow-hidden">
            <PageHeader title={menuTitle} />
            <TabBar parentCode="tenant" />

            {/* アクションボタン（編集権限ありのみ表示） */}
            {canEdit && (
                <div className="px-6 pb-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={() => navigate("/tenant/holiday/new")}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500 text-white text-base font-medium hover:bg-brand-600 transition-colors"
                        >
                            <ActionIcon name="add" className="w-4 h-4" />
                            新規登録
                        </button>
                        <button
                            type="button"
                            disabled
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-300 text-gray-500 text-base font-medium cursor-not-allowed"
                        >
                            <ActionIcon name="upload" className="w-4 h-4" />
                            アップロード
                        </button>
                        <button
                            type="button"
                            disabled
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-300 text-gray-500 text-base font-medium cursor-not-allowed"
                        >
                            <ActionIcon name="download" className="w-4 h-4" />
                            ダウンロード
                        </button>
                    </div>
                </div>
            )}

            {/* テーブル */}
            <div className="flex-1 overflow-hidden px-6 pb-6">
                <DayOffTypeTable rows={rows} isLoading={isLoading} onEditRow={handleEditRow} />
            </div>
        </div>
    );
};

export default TenantHolidayPage;
